import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { SALES } from '@rma/generic/domain/types';
import {
  GOOGLE_MAPS_STATIC_MARKER,
  type MapMarkerStatus,
  type MapMarkerType,
  type RmaMapMarker,
  type RmaMapMarkerConfig,
  type RmaMapMarkerDetail,
} from '@rma/generic/maps/domain';
import { CoordsToMapMarkerPipe, MapMarkerService } from '@rma/generic/maps/util';
import { StaticImageUrlPipe } from '@rma/public/generic/util/environment';
import { fromPartial } from '@total-typescript/shoehorn';
import { uniqBy } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';
import type { MarketReportReview } from '../domain';
import { BackToMapDirective } from '../ui-back-to-map/back-to-map.directive';
import { MarketReportReviewTileComponent } from './ui-market-report-review-tile/market-report-review-tile.component';
import { MarketReportReviewsMapComponent } from './ui-market-report-reviews-map/market-report-reviews-map.component';

@Component({
  selector: 'rma-market-report-reviews',
  templateUrl: './market-report-reviews.component.html',
  styleUrls: ['./market-report-reviews.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    BackToMapDirective,
    MarketReportReviewTileComponent,
    MarketReportReviewsMapComponent,
    StaticImageUrlPipe,
    TranslocoModule,
  ],
  providers: [CoordsToMapMarkerPipe, MapMarkerService],
})
export class MarketReportReviewsComponent implements OnChanges {
  @Input()
  primaryMapMarker: RmaMapMarker | null | undefined;

  @Input()
  reviews: MarketReportReview[] | null | undefined;

  @Input()
  isReportOwner: boolean;

  @Input()
  isPrintMode: boolean | null = false;

  @Input()
  printedUrl: string | undefined;

  private readonly mapMarkerService = inject(MapMarkerService);
  private readonly coordsToMapMarkerPipe = inject(CoordsToMapMarkerPipe);
  private readonly document = inject(DOCUMENT);
  private readonly markerType: MapMarkerType = 'Review';

  markers$: Observable<RmaMapMarker[]>;
  selectedItemId?: string;
  selectedElement: HTMLElement | null;

  private markersSource = new BehaviorSubject<RmaMapMarker[]>([]);

  constructor() {
    this.markers$ = this.markersSource.asObservable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { reviews } = changes;

    if (reviews && this.reviews) {
      const markers = this.mapToRmaMapMarkers(this.reviews, this.isPrintMode ?? false);

      if (markers.length) {
        if (this.primaryMapMarker) {
          const primaryMarker = {
            ...this.primaryMapMarker,
            content: this.mapMarkerService.getContent(GOOGLE_MAPS_STATIC_MARKER),
          };

          markers.unshift(primaryMarker);
          markers.unshift(this.primaryMapMarker);
        }

        const coordCampaigns = markers.map((marker) => ({
          ...marker,
          latlon: JSON.stringify(marker.detail.coords),
        }));

        const uniqueMarkers = uniqBy(coordCampaigns, 'latlon');

        this.markersSource.next(uniqueMarkers);
      }
    }
  }

  onMarkerSelected(itemId: string | string[]) {
    const selectedItemId = Array.isArray(itemId) ? itemId[0] : itemId;
    this.setSelectedItemId(selectedItemId);
    this.markersSource.next(this.setSelectedMarker(this.markersSource.value, selectedItemId));
  }

  setSelectedItemId(itemId: string) {
    this.selectedItemId = this.selectedItemId !== itemId ? itemId : undefined;

    if (this.selectedItemId) {
      this.selectedElement = this.document.getElementById(itemId);

      if (this.selectedElement) {
        this.selectedElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  private mapToRmaMapMarkers(reviews: MarketReportReview[], isStaticMap: boolean): RmaMapMarker[] {
    return reviews
      .filter((c) => (c.latitude ?? 0) !== 0 && (c.longitude ?? 0) !== 0)
      .map((review, i) => this.mapToRmaMapMarker(review, isStaticMap, i + 1));
  }

  private mapToRmaMapMarker(review: MarketReportReview, isStaticMap: boolean, markerIndex: number): RmaMapMarker {
    if (isStaticMap) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this.coordsToMapMarkerPipe.transform(
        {
          lat: review.latitude ?? 0,
          lon: review.longitude ?? 0,
        },
        { status: 'standard', mapType: this.markerType },
      )!;
    }

    const detail: RmaMapMarkerDetail = {
      id: review.reviewId.toString(),
      combinedHash: review.reviewId,
      title: review.fullAddress,
      codes: [review.reviewId.toString()],
      coords: {
        lat: review.latitude ?? 0,
        lon: review.longitude ?? 0,
      },
      markerCount: 1,
      status: 'standard',
      hovered: false,
      verticalType: SALES,
      info: [],
      markerType: this.markerType,
    };

    const { content, zIndex, gmpClickable } = this.mapMarkerService.transform(
      fromPartial({ markerType: detail.markerType }),
      detail.status,
      markerIndex,
    );

    return {
      detail,
      position: { lat: detail.coords.lat, lng: detail.coords.lon },
      title: detail.title,
      content,
      zIndex,
      options: {
        gmpClickable,
      },
    };
  }

  private setSelectedMarker(markers: RmaMapMarker[], itemId: string): RmaMapMarker[] {
    return markers.map((marker, i) => {
      if (marker.detail.status === 'active') {
        const { content } = this.getDefaultMarker(marker.zIndex ?? i, 'viewed');
        return {
          ...marker,
          content,
          detail: { ...marker.detail, status: 'viewed' },
          options: { ...marker.options, gmpClickable: true },
        };
      }

      if (marker.detail.id === itemId) {
        return {
          ...marker,
          content: this.mapMarkerService.transform(fromPartial({ markerType: this.markerType }), 'active', marker.zIndex ?? i).content,
          options: { ...marker.options, gmpClickable: false },
          detail: { ...marker.detail, status: 'active' },
        };
      }

      return marker;
    });
  }

  private getDefaultMarker(markerIndex: number, status: MapMarkerStatus = 'standard'): RmaMapMarkerConfig {
    return {
      ...this.mapMarkerService.transform(fromPartial({ markerType: this.markerType }), status, markerIndex),
      gmpClickable: true,
    };
  }
}
